// Site plan ("plan de situație", A01): the on-grade layer of a solution, drawn
// to the conventions of the reference sheet rather than as a coloured diagram.
//
// The layer arrives pre-computed in `site` — bars, roads, stalls, sidewalks, two
// classes of green, areas, the street access. What the reference ALSO draws, and
// no upstream stage computes, is derived in `siteDerive`: the asphalt apron under
// each parking row, the tree canopies over the planting, give-way plates, painted
// arrows, the zebra at the access, dimension chains and the numbered boundary.
// The glyphs themselves live in `siteGlyphs`, together with the A01 palette and
// the list of SVG features the PDF exporter does not implement.
//
// Pure drawing: nothing here solves anything, and nothing here calls Math.random.
import { memo, useMemo } from "react";
import type { Pt } from "../types";
import type { SiteDoor, SiteEntrance, SiteLayer, SiteShape, SiteStair } from "../client";
import { pickAttrs, type PickAttrs, type Selection } from "../inspect";
import { DECOR } from "./glyphs";
import {
  A01, ApronLines, ApronPad, CompassRose, CotaCorpView, DimSpanView, FasiePmrPoly, GateMark, GreenBalance,
  LaneDashes, Plate, RampMark, RampMouthMark, RoadArrowMark, SANS, SignPlate, SiteBay,
  SiteStallNumber, TreeCanopy, VertexMark, ViewTurn, ZebraMark, ZebraTrecereMark, ZonaSimbol, useViewTurn,
} from "./siteGlyphs";
import {
  barRings, boundaryMarks, centroid, labelSpot, roadAxis, roadRing, runChain,
  copaciiDocumentului, obstacolePlantatie, plantatie, runCorners, sensUnic, siteRamps, siteSignage, siteStalls, stallRuns,
  zebraTrecere, GOL_TRECERE_M,
} from "./siteDerive";
import { coteleSitului } from "./coteSit";
import { carosabil, dilata } from "./carosabil";
import { caleHasura, liniiHasuraMulti } from "./hasura";
import { numeCorpFunctiune } from "./arhLegenda";
import { t } from "../../i18n";

/** Rendered-pixel thresholds below which a layer is dropped. A 4 px stall number
 *  is noise, and a sheet's worth of dimension chains at whole-lot zoom is a grey
 *  smear — the reference is read at 1:500 on a plotted A1, not in a viewport. */
// `barLabel` is the rendered height of the block label in pixels. Six, not
// eight: the label is sized to the room the building actually has now, which is
// smaller than the bounding box it used to be measured against, and the fit no
// longer clamps the zoom — together those pushed a label that used to show at
// the opening view just under the old threshold.
// `cota` e mai jos decât `chain` cu un ordin de mărime, și trebuie să fie:
// lanțul locurilor se citește lipit de desen, iar distanțele dintre corpuri se
// verifică exact când planul ÎNTREG e pe ecran. Măsurat pe planul
// beneficiarului, tot lotul de 300 m intră pe la 4,5 px/m — deci pragul stă sub
// el, altfel cotele apar abia după ce nu mai vezi decât două blocuri.
const LOD = { stallNo: 7, barLabel: 6, car: 2.2, sign: 3.2, chain: 22, cota: 3.0 };
// cât se rupe axa punctată de o parte și de alta a unei treceri de pietoni:
// plăcuța săgeții de sens are 2,55 m (`RoadArrowMark`), deci centrul ei la
// 1,5 m de zebră o lasă întreagă pe asfalt

const pts = (ring: number[][]) => ring.map((p) => `${p[0]},${p[1]}`).join(" ");

/** Până unde ajunge, din `p` pe direcția `u`, o rampă a scării în cutia casei de
 *  scară: punctul de pe contur (prima latură tăiată), tras înapoi cu grosimea
 *  peretelui; `null` când raza nu taie conturul. */
function panaLaPerete(p: [number, number], u: [number, number], contur: number[][], perete = 0.15):
  { capat: [number, number]; pePerete: [number, number]; t: number } | null {
  let best: number | null = null;
  for (let i = 0; i < contur.length; i++) {
    const a = contur[i], b = contur[(i + 1) % contur.length];
    const ex = b[0] - a[0], ey = b[1] - a[1];
    const den = u[0] * ey - u[1] * ex;
    if (Math.abs(den) < 1e-9) continue;
    const t = ((a[0] - p[0]) * ey - (a[1] - p[1]) * ex) / den;
    const r = ((a[0] - p[0]) * u[1] - (a[1] - p[1]) * u[0]) / den;
    if (t > 1e-6 && r >= -1e-6 && r <= 1 + 1e-6 && (best === null || t < best)) best = t;
  }
  if (best === null) return null;
  return { pePerete: [p[0] + u[0] * best, p[1] + u[1] * best],
           capat: [p[0] + u[0] * Math.max(0, best - perete), p[1] + u[1] * Math.max(0, best - perete)], t: best };
}

/** Casa scării de evacuare a parcajului, la sol (formatul 3.16, §35), ca o scară
 *  exterioară: cutia ei cu pereții, pe hârtie, conturul negru al clădirilor;
 *  înăuntru, fiecare rampă a scării cu treptele ei (linii de-a latul, la pasul
 *  treptei, pe lățimea rampei), ÎNTINSĂ până în peretele podestului de întoarcere
 *  — documentul dă rampa dintre podeste, dar la sol golul de scară are trepte
 *  până în perete, iar alb rămâne doar capătul tamponului (SAS), de unde se iese
 *  (Oana, 24.10.2026). Săgeata de urcare pe
 *  axă. IEȘIREA la teren: ușa pe peretele în care se termină ultima rampă (de
 *  jos în sus), desenată ca intrarea de bloc (`UsaBloc`), ca să se vadă că e
 *  o ușă de care se va lega o alee. Zona fără parcare la sol (`zonaM`) NU se
 *  desenează (Oana, 24.10.2026): o spune inspectorul. Cutia se apasă (`siteStair`). */
function ScaraEvacuareMark({ s, i, showLabel, on, pick }: {
  s: SiteStair; i: number; showLabel: boolean; on: boolean; pick: (sel: Selection) => PickAttrs;
}) {
  const turn = useViewTurn();
  const c = s.contur;
  const cx = c.reduce((a, p) => a + p[0], 0) / c.length;
  const cy = c.reduce((a, p) => a + p[1], 0) / c.length;
  const w = s.latimeM ?? 0.9, pas = s.treaptaM ?? 0.28;
  // rampele vin de jos în sus: între două rampe la rând e podestul de întoarcere, iar la
  // celălalt capăt stă tamponul (SAS). La sol treptele se desenează până în peretele
  // podestului, iar capătul tamponului rămâne alb (Oana, 24.10.2026): se întind capetele
  // dinspre podest, nu pornirea primei rampe și nici capătul ultimei
  const liber = (k: number, capat: "de" | "la") =>
    capat === "de" ? k > 0 : k < s.rampe.length - 1;
  const trepte: [number, number, number, number][] = [];
  const sageti: [number, number, number, number][] = [];
  let usa: SiteDoor | null = null;
  s.rampe.forEach((r, k) => {
    const dx = r.la[0] - r.de[0], dy = r.la[1] - r.de[1];
    const L0 = Math.hypot(dx, dy);
    if (L0 < 1e-6) return;
    const ux = dx / L0, uy = dy / L0, nx = -uy, ny = ux;
    // capetele libere, întinse până la perete
    let de: [number, number] = r.de, la: [number, number] = r.la;
    if (liber(k, "de")) de = panaLaPerete(r.de, [-ux, -uy], c)?.capat ?? de;
    if (liber(k, "la")) la = panaLaPerete(r.la, [ux, uy], c)?.capat ?? la;
    const L = Math.hypot(la[0] - de[0], la[1] - de[1]);
    for (let t = pas; t < L - 1e-6; t += pas) {
      const x = de[0] + ux * t, y = de[1] + uy * t;
      trepte.push([x - nx * w / 2, y - ny * w / 2, x + nx * w / 2, y + ny * w / 2]);
    }
    sageti.push([de[0] + ux * 0.15, de[1] + uy * 0.15, la[0] - ux * 0.15, la[1] - uy * 0.15]);
    // ieșirea: pe peretele de la capătul ultimei rampe (capătul tamponului) e ușa spre teren
    const sus = k === s.rampe.length - 1 ? panaLaPerete(r.la, [ux, uy], c) : null;
    if (sus) {
      usa = { eid: `${s.id}-iesire`, cx: sus.pePerete[0], cy: sus.pePerete[1],
              deg: (Math.atan2(ny, nx) * 180) / Math.PI, w: Math.max(w, 0.9), n: [ux, uy] };
    }
  });
  const lx = cx, ly = cy - (c.reduce((a, p) => Math.max(a, Math.abs(p[1] - cy)), 0) + 0.7);
  return (
    <g>
      <polygon points={pts(c)} fill={A01.paper} stroke={on ? A01.red : A01.ink}
               strokeWidth={on ? 0.3 : 0.18} strokeLinejoin="miter"
               style={{ cursor: "pointer" }} {...pick({ kind: "siteStair", level: "site", i })} />
      <g pointerEvents="none">
        {trepte.map(([x1, y1, x2, y2], k) => (
          <line key={`t${k}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={A01.ink} strokeWidth={0.04} />
        ))}
        {sageti.map(([x1, y1, x2, y2], k) => {
          const L = Math.hypot(x2 - x1, y2 - y1);
          if (L < 0.5) return null;
          const ux = (x2 - x1) / L, uy = (y2 - y1) / L, a = Math.min(0.25, L * 0.25);
          return (
            <g key={`s${k}`}>
              <line x1={x1} y1={y1} x2={x2 - ux * a} y2={y2 - uy * a} stroke={A01.ink} strokeWidth={0.06} />
              <polygon points={`${x2},${y2} ${x2 - ux * a - uy * a * 0.5},${y2 - uy * a + ux * a * 0.5} ${x2 - ux * a + uy * a * 0.5},${y2 - uy * a - ux * a * 0.5}`}
                       fill={A01.ink} />
            </g>
          );
        })}
        {usa && <UsaBloc u={usa} />}
        {showLabel && (
          <text x={lx} y={ly} fontSize={0.9} textAnchor="middle" dominantBaseline="central"
                fill={A01.ink} fontFamily="Helvetica, Arial, sans-serif" fontWeight={700}
                transform={`rotate(${-turn} ${lx} ${ly})`}>{s.id}</text>
        )}
      </g>
    </g>
  );
}

/** A point the drawing can use. `entries` is the list that arrives with a
 *  degenerate member — an empty array where a point should be — and it goes
 *  straight into a glyph, which puts `translate(NaN NaN)` into the markup. The
 *  bays are screened by `siteStalls`, which drops what it cannot place. */
const at2 = (a?: number[]): a is number[] =>
  Array.isArray(a) && Number.isFinite(a[0]) && Number.isFinite(a[1]);


/** One SVG path from a list of rings. */
const pathD = (rings: number[][][]) =>
  (rings ?? [])
    .filter((r) => r && r.length >= 3)
    .map((r) => "M" + r.map((p) => `${p[0]} ${p[1]}`).join(" L") + "Z")
    .join(" ");

/** One ground surface per <path>, its holes punched with `evenodd`.
 *
 *  ONE path per surface rather than one path for the layer. Two reasons, and
 *  both are visible on a real site plan: a joined path cannot hold holes (an
 *  apron drew as a filled block over the planting it surrounds), and under
 *  either fill rule the aprons of two buildings that touch would cancel where
 *  they overlap. Separate paths just paint over each other, which is what a plan
 *  does. */
function Surfaces({ shapes, fill, fillOpacity }:
    { shapes?: SiteShape[]; fill: string; fillOpacity?: number }) {
  return (
    <>
      {(shapes ?? []).map((sh, i) => {
        const ring = Array.isArray(sh) ? sh : sh?.poly;
        if (!ring || ring.length < 3) return null;
        const holes = Array.isArray(sh) ? [] : (sh.holes ?? []);
        return (
          <path key={i} d={pathD([ring, ...holes])} fillRule="evenodd"
            fill={fill} fillOpacity={fillOpacity} />
        );
      })}
    </>
  );
}

/** One building band: the footprint, and its height regime set on two lines —
 *  "P+11" over "H=33.6m", as every bar on the reference sheet is labelled.
 *  A FUNCTION BODY (school, offices, standalone retail) carries its use as a
 *  line of its own, between the code and the regime: on the sheet that is
 *  what tells it apart from the dwellings around it. */
function BarView({ pts: poly, label, h, code, functiune, pxPerM, pick, selected }:
  { pts: number[][]; label: string; h: number; code?: string; functiune?: string;
    pxPerM: number; pick?: PickAttrs; selected?: boolean }) {
  if (!poly || poly.length < 3) return null;
  const spot = labelSpot(poly);
  const rows = [code, functiune ? numeCorpFunctiune(functiune) : undefined, label, `H=${h}m`]
    .filter(Boolean) as string[];
  const longest = Math.max(...rows.map((r) => r.length));
  // The block of text has to fit the circle that fits the building, so the
  // DIAGONAL of the block is what is bounded, not its width and height
  // separately. `0.86` leaves it a margin off the walls rather than touching
  // them, which is the difference between fitting and looking like it fits.
  const cx = spot?.x ?? 0, cy = spot?.y ?? 0;
  const diag = Math.hypot(longest * 0.62, rows.length * 1.25);
  const fs = Math.min(1.8, ((spot?.r ?? 0) * 2 * 0.86) / diag);
  const showLabel = !!spot && fs * pxPerM >= LOD.barLabel && fs >= 0.45;
  const turn = useViewTurn();
  return (
    <g {...pick}>
      {/* A HEAVY outline. The block is the one thing on a site plan that is not
          ground, and the reference draws its edge at roughly twice the weight of
          anything paved — at a whole-lot zoom a 0.16 m line put the building on
          the same footing as a kerb. */}
      <polygon points={pts(poly)} fill={A01.paved} stroke={A01.ink}
        strokeWidth={0.3} strokeLinejoin="round" />
      {selected && (
        <polygon points={pts(poly)} fill="none" stroke="var(--sel-line)"
          strokeWidth={0.5} strokeLinejoin="round" />
      )}
      {showLabel && (
        <g transform={`rotate(${-turn} ${cx} ${cy})`}>
          <text x={cx} y={cy - fs * (rows.length - 1) * 0.62} fontSize={fs}
            fontFamily={SANS} fontWeight={700} fill={A01.ink}
            textAnchor="middle" {...DECOR}>
            {rows.map((r, i) => (
              <tspan key={r} x={cx} dy={i === 0 ? 0 : fs * 1.25}>{r}</tspan>
            ))}
          </text>
        </g>
      )}
    </g>
  );
}

/** Street access: a red pediment pointing into the lot + an "acces" caption. */
function EntryMark({ x, y, cx, cy }:
  { x: number; y: number; cx: number; cy: number }) {
  const a = Math.atan2(cy - y, cx - x);          // inward = toward lot centroid
  const ux = Math.cos(a), uy = Math.sin(a);
  const px = -uy, py = ux;
  return (
    <g>
      <polygon fill={A01.red}
        points={`${x + ux * 2.4},${y + uy * 2.4} ${x + px * 1.6},${y + py * 1.6} ${x - px * 1.6},${y - py * 1.6}`} />
      <Plate x={x - ux * 2.6} y={y - uy * 2.6} lines={["acces", "carosabil"]} fs={1.0} />
    </g>
  );
}

/** Building access ("acces clădire"): the porch slab on the facade, a red
 *  pediment pointing out of the building toward the parking band, and the
 *  caption on its plate. `deg` is the stall convention — heading = (sin, -cos). */
/** O ușă de intrare în bloc, cum o desenează planșa de nivel: golul în perete
 *  și DOUĂ foi, câte una din fiecare toc, deschise la 90° spre EXTERIOR (ușile
 *  de evacuare se deschid în sensul ieșirii) — arcurile de balans punctate se
 *  întâlnesc la mijlocul golului. Ușa dublă se citește de departe ca intrare,
 *  o foaie singură se confunda cu o ușă oarecare (Oana, 30.09.2026). Dedesubt,
 *  săgeata albastră de intrare, din curte spre ușă. */
function UsaBloc({ u }: { u: SiteDoor }) {
  const a = (u.deg * Math.PI) / 180;
  const ux = Math.cos(a), uy = Math.sin(a);               // de-a lungul foii închise
  const [nx, ny] = u.n;                                   // spre exterior
  const s: [number, number] = [u.cx - ux * u.w / 2, u.cy - uy * u.w / 2];
  const e: [number, number] = [u.cx + ux * u.w / 2, u.cy + uy * u.w / 2];
  const mid: [number, number] = [u.cx, u.cy];
  const foaie = u.w / 2;
  // o foaie: balamaua într-un toc, vârful la 90° spre exterior, arcul de la vârf
  // la mijlocul golului, pe drumul scurt în jurul balamalei
  const Foaie = ({ hinge }: { hinge: [number, number] }) => {
    const tip: [number, number] = [hinge[0] + nx * foaie, hinge[1] + ny * foaie];
    const cross = (tip[0] - hinge[0]) * (mid[1] - hinge[1]) - (tip[1] - hinge[1]) * (mid[0] - hinge[0]);
    const sweep = cross > 0 ? 1 : 0;
    return (
      <>
        <line x1={hinge[0]} y1={hinge[1]} x2={tip[0]} y2={tip[1]} stroke={A01.ink} strokeWidth={0.1} />
        <path d={`M${tip[0]} ${tip[1]} A${foaie} ${foaie} 0 0 ${sweep} ${mid[0]} ${mid[1]}`}
              fill="none" stroke={A01.ink} strokeWidth={0.06} strokeDasharray="0.25 0.18" />
      </>
    );
  };
  // săgeata: din curte spre ușă, cu vârful la 0,3 m de perete
  const L = 2.2, W = 1.0;
  const vx = u.cx + nx * 0.3, vy = u.cy + ny * 0.3;
  const bx = u.cx + nx * (0.3 + L), by = u.cy + ny * (0.3 + L);
  return (
    <g {...DECOR}>
      <line x1={s[0]} y1={s[1]} x2={e[0]} y2={e[1]} stroke={A01.paper} strokeWidth={0.32} />
      <Foaie hinge={s} />
      <Foaie hinge={e} />
      <polygon fill={A01.signBlue}
        points={`${vx},${vy} ${bx + ux * W / 2},${by + uy * W / 2} ${bx + ux * W * 0.18},${by + uy * W * 0.18} ${bx + ux * W * 0.18 + nx * L * 0.7},${by + uy * W * 0.18 + ny * L * 0.7} ${bx - ux * W * 0.18 + nx * L * 0.7},${by - uy * W * 0.18 + ny * L * 0.7} ${bx - ux * W * 0.18},${by - uy * W * 0.18} ${bx - ux * W / 2},${by - uy * W / 2}`} />
    </g>
  );
}

function BuildingEntrance({ e, pxPerM, pick, selected, faraSageata = false }:
  { e: SiteEntrance; pxPerM: number; pick?: PickAttrs; selected?: boolean; faraSageata?: boolean }) {
  // A facing direction is not guaranteed. An upstream stage places entrances and
  // may know only WHERE a door is, not which way it opens — and one
  // `Math.sin(undefined)` puts NaN into every coordinate derived from it, which
  // is twelve NaN attributes in the emitted SVG rather than one missing arrow.
  // So the arrow is what is skipped; the porch and the caption still draw.
  const known = Number.isFinite(e.deg as number);
  const a = ((e.deg ?? 0) * Math.PI) / 180;
  const ux = Math.sin(a), uy = -Math.cos(a);         // outward
  const px = -uy, py = ux;                           // along the facade
  return (
    <g {...pick}>
      {e.porch?.length >= 3 && (
        <polygon points={pts(e.porch)} fill={A01.walk} stroke={A01.ink}
          strokeWidth={0.12} />
      )}
      {selected && e.porch?.length >= 3 && (
        <polygon points={pts(e.porch)} fill="none" stroke="var(--sel-line)"
          strokeWidth={0.36} />
      )}
      {known && !faraSageata && (
        <polygon fill={A01.red}
          points={`${e.x + ux * 5.4},${e.y + uy * 5.4} ${e.x + ux * 2.6 + px * 2.0},${e.y + uy * 2.6 + py * 2.0} ${e.x + ux * 2.6 - px * 2.0},${e.y + uy * 2.6 - py * 2.0}`} />
      )}
      {known && !faraSageata && 1.0 * pxPerM >= LOD.barLabel && (
        <Plate x={e.x + ux * 8.4} y={e.y + uy * 8.4}
          lines={[t("a01.acces"), t("a01.acces_cladire")]} fs={1.0} />
      )}
    </g>
  );
}

/** A 5 m scale bar in the A01 idiom — alternating black and white cells with the
 *  run written under it. Metre stroke widths: the exporter drops
 *  `non-scaling-stroke`, so a "1 px" hairline prints as a metre of ink. */
function SiteScaleBar({ x, y }: { x: number; y: number }) {
  const cell = 5, n = 4, h = 0.7;
  return (
    <g {...DECOR}>
      {Array.from({ length: n }, (_, i) => (
        <rect key={i} x={x + i * cell} y={y} width={cell} height={h}
          fill={i % 2 === 0 ? A01.ink : "#ffffff"}
          stroke={A01.ink} strokeWidth={0.08} />
      ))}
      {Array.from({ length: n + 1 }, (_, i) => (
        <text key={i} x={x + i * cell} y={y + h + 1.2} fontSize={0.85}
          fontFamily={SANS} textAnchor="middle" fill={A01.ink}>{i * cell}</text>
      ))}
      <text x={x + n * cell + 1.4} y={y + h + 1.2} fontSize={0.85}
        fontFamily={SANS} fill={A01.ink}>m</text>
    </g>
  );
}

function SitePlanView({ site, lot, pxPerM = 40,
                                       interactive = false, selected,
                                       showCompass = true, showSheet = true,
                                       arataCote = true,
                                       arataRetrageri = false,
                                       viewTurn = 0 }:
  { site: SiteLayer; lot: Pt[]; pxPerM?: number;
    /** Mark the surface stalls, the building bands and the "acces clădire"
     *  marks so the Editor can name whichever is pressed. Off on the PDF export
     *  path, which renders statically. */
    interactive?: boolean; selected?: Selection | null;
    /** The rose belongs on a PLOTTED sheet, so it defaults on. The interactive
     *  viewer turns it off and floats its own beside the canvas: there it has to
     *  keep a fixed size whatever the zoom, and turn on its own when the drawing
     *  is rotated — neither of which a glyph drawn in metres inside the plan can
     *  do. */
    showCompass?: boolean;
    /** The green balance and the scale bar. Same story as the rose: they belong
     *  on a PLOTTED sheet, so they default on, and the interactive viewer turns
     *  them off and floats its own beside the canvas — where they keep a fixed
     *  size, do not turn with the drawing, and the bar can follow the zoom. */
    showSheet?: boolean;
    /** Cotele așezării: între corpuri și până la limita de proprietate. Pornite,
     *  fiindcă sunt primul lucru pe care îl verifică cineva care se uită la o
     *  așezare — dar se pot stinge, ca orice strat, fiindcă pe o planșă tipărită
     *  pentru altceva (verde, parcaj) sunt zgomot. Se sting singure sub
     *  `LOD.cota`. */
    arataCote?: boolean;
    /** Hașurează banda în care NU se poate construi — lotul, mai puțin zona
     *  edificabilă a motorului. Stinsă la pornire: e o verificare, nu un strat
     *  al planșei. */
    arataRetrageri?: boolean;
    /** How far the VIEWER has turned the drawing on screen. Lettering cancels it
     *  so it keeps the orientation it was designed with — see `ViewTurn`. Zero
     *  on the plotted sheet, which never turns. */
    viewTurn?: number }) {
  // EVERYTHING derived, once per document.
  //
  // It used to be recomputed on every render, and the viewer re-renders this
  // component on every frame of a turn — `viewTurn` changes, so the whole
  // subtree does. Measured on a real scheme that is 3,35 ms of arithmetic per
  // frame, a fifth of the frame gone before a single element is touched, and
  // 2,3 ms of it is scattering the trees. None of it depends on the turn or on
  // the zoom: it depends on the geometry, and the geometry does not move.
  const selSite = selected && selected.level === "site" ? selected : null;
  const d = useMemo(() => {
    if (!site || lot.length < 3) return null;
    // fiecare alee cu axa ei dreaptă (sau fără, la traseele curbe), corpul
    // ei și, dacă e curbă sau frântă, traseul: ACELAȘI inel merge și în asfalt
    // și în `corpuri`, ca `carosabil` să-și recunoască propriul corp
    const piese = (site.roads ?? []).map((r) => ({
      r, axa: roadAxis(r), corp: roadRing(r) ?? undefined,
      traseu: (!Array.isArray(r) && r.path && r.path.length > 2) ? (r.path as Pt[]) : undefined,
    }));
    const cuAxa = piese.filter((p) => p.axa !== null);
    const roadAxes = cuAxa.map((p) => p.axa!);
    // The bays, with their geometry RECOVERED from the footprint the producer
    // drew rather than from the `deg` it declared — see `siteStalls`. The rows
    // are then grouped against the roads, which is what settles which way the
    // cars face.
    const runs = stallRuns(siteStalls(site.stalls), roadAxes);
    const bars = barRings(site.bars);
    const roads = piese.map((p) => p.corp).filter((r): r is number[][] => !!r);
    const ramps = siteRamps(site.ramps);
    // reuniunea asfaltului: drumurile (și piesele fără axă) plus rampele
    const asfalt = [...roads, ...ramps.map((r) => r.poly)];
    // pe un document al agentului (`diagnostics.agent`) plăcuțele euristice
    // (cedează / acces interzis / pericol) n-au sens: sensurile le-a hotărât el
    // și intersecțiile nu sunt cele presupuse de euristică (Oana, 30.10.2026);
    // săgețile de sens de pe carosabil rămân
    const semne0 = siteSignage(site.roads, lot, site.entries,
                               [...bars, ...runs.map(runCorners)]);
    return {
      roadAxes, runs, bars, roads, ramps,
      // pe unde merge axa punctată: tăiată la T, arcuită la colț, semicerc la
      // întoarcerea în rampă, S între alei decalate; traseele curbe pe
      // polilinia lor; totul tăiat la corpul altor drumuri și la asfalt
      // (`carosabil.ts`); aleile cu sens unic dau săgeți în loc de liniuțe
      car: carosabil(roadAxes, ramps.map((r) => r.poly), lot,
                     { curburi: site.curburi !== false, asfalt,
                       corpuri: cuAxa.map((p) => p.corp),
                       // zebra rupe axa: nici liniuțe, nici săgeți de sens peste trecere
                       // (Oana, 31.10.2026); golul începe cu GOL_TRECERE_M înaintea benzii
                       goluri: (site.crossings ?? []).map((c) => dilata(c.poly, GOL_TRECERE_M)),
                       trasee: piese.filter((p) => p.traseu)
                         .map((p) => ({ linie: p.traseu!, corp: p.corp, twoWay: !sensUnic(p.r) })) }),
      // copacii documentului (topo + fâșiile agentului) întâi, presărarea în jurul lor
      trees: plantatie(site.green, obstacolePlantatie(site), lot ?? null, copaciiDocumentului(site.trees)),
      // zebrele trecerilor de pietoni ale agentului (3.18): dungile, o dată,
      // tăiate la asfalt (banda documentului iese 0,50 m peste bordură)
      zebre: (site.crossings ?? []).map((c) => zebraTrecere(c, asfalt)),
      // The aprons block signage too: a give-way plate painted across bay 3 is
      // not a plan, and the roads-only test cannot see the parking because the
      // stalls are not part of the road network.
      //
      // Named, not spread: `boundaryMarks` also returns a `runs`, and spreading
      // both would have silently handed the parking rows the boundary's.
      semne: site.agent ? { ...semne0, signs: [] } : semne0,
      contur: boundaryMarks(lot),
      // Cotele așezării — între corpuri și până la limită. Aici, în memo, ca
      // tot restul: rotirea planului nu le schimbă, iar recalculate la fiecare
      // cadru ar costa O(corpuri²) de geometrie pe o rotire care nu mișcă
      // niciun corp.
      cote: coteleSitului(bars, lot, runs.map(runCorners), roads),
      // BANDA INTERZISĂ: lotul, mai puțin zona edificabilă — și hașurată prin
      // aceeași regulă par-impar, căreia îi dai lotul urmat de zonă și-ți lasă
      // gol exact ce e construibil (v. `liniiHasuraMulti`).
      //
      // Zona vine din DOCUMENT, calculată de motor (`Area`, `kind:
      // "buildable"`), nu refăcută aici din retrageri: dacă ecranul ar
      // recalcula-o, ar putea arăta altceva decât a folosit generarea, adică
      // fix minciuna pe care verificarea asta există s-o prindă.
      interzis: (site.edificabil?.length ?? 0) >= 1
        ? liniiHasuraMulti([lot as number[][], ...site.edificabil!], 4, 45)
        : [],
    };
  }, [site, lot]);

  // The two heaviest groups, held still across a turn.
  //
  // Rotating changes only `viewTurn`, and only LETTERING reads it — but React
  // rebuilds every element this component returns, so all 5.000-odd of them
  // reconciled on every frame. The bays are about 1.600 of those and the
  // canopies another 400, and neither has anything to do with the turn. Held in
  // a memo their elements keep their identity, so React walks past them.
  const bayJSX = useMemo(() => !d ? null : d.runs.flatMap((r) => r.stalls.map((s) => (
    <SiteBay key={`st${s.i}`} s={s}
      pick={interactive ? pickAttrs({ kind: "stall", level: "site", i: s.i }) : {}}
      selected={selSite !== null && selSite.kind === "stall"
                && (selSite as { i?: number }).i === s.i} />
  ))), [d, interactive, selSite]);
  const treeJSX = useMemo(() => !d ? null :
    d.trees.map((t, i) => <TreeCanopy key={`tr${i}`} t={t} />), [d]);

  if (!site || lot.length < 3 || !d) return null;
  const { runs, roads, ramps } = d;
  const { signs, marks, crossings } = d.semne;
  const { vertices, runs: edgeRuns } = d.contur;

  const sel = selSite;
  const at = (kind: Selection["kind"], i: number) =>
    // `sel.i` read optionally: a `storey` selection has no index, and it never
    // matches a drawn shape — it means "this whole floor".
    sel !== null && sel.kind === kind && (sel as { i?: number }).i === i;
  const pick = (s: Selection): PickAttrs => interactive ? pickAttrs(s) : {};

  const lotPts = pts(lot);
  const [lcx, lcy] = centroid(lot);
  const xs = lot.map((p) => p[0]), ys = lot.map((p) => p[1]);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const furnitureY = maxY + 4;
  const compassY = minY - 9;
  // Numbering runs along each row, the way a sheet numbers a car park.
  let seq = 0;

  const showCars = pxPerM >= LOD.car;
  const showNumbers = 1.05 * pxPerM >= LOD.stallNo;
  const showSigns = pxPerM >= LOD.sign;
  const showChains = pxPerM >= LOD.chain;
  const showCote = arataCote && pxPerM >= LOD.cota;

  return (
    <ViewTurn.Provider value={viewTurn}>
    <g className="pg-site">
      <defs>
        {/* the sidewalk's running-bond paving, as the reference hatches it */}
        <pattern id="pg-site-pavers" width={1.2} height={0.6}
                 patternUnits="userSpaceOnUse">
          <path d="M0 0H1.2M0 0.3H1.2M0.3 0V0.3M0.9 0.3V0.6"
            stroke={A01.paver} strokeWidth={0.05} fill="none" />
        </pattern>
        {/* the planting stipple: scattered ticks and open triangles, the
            standard vegetation hatch on this sheet */}
        <pattern id="pg-site-grass" width={3.2} height={3.2}
                 patternUnits="userSpaceOnUse">
          <g stroke="#4a5f3c" strokeWidth={0.06} fill="none" opacity={0.5}>
            <path d="M0.5 0.7l0.28 -0.16M2.1 0.35l0.22 0.2M1.35 1.9l-0.3 0.12
                     M2.75 2.35l0.2 0.18M0.35 2.6l0.26 -0.1M1.8 2.95l0.24 0.14" />
            <path d="M1.6 0.55l0.34 0.6h-0.68zM0.7 2.0l0.3 0.52h-0.6z
                     M2.5 1.35l0.32 0.55h-0.64z" />
          </g>
        </pattern>
        {/* the arrow plates: painted asphalt reads as speckled on the sheet,
            not as flat grey */}
        <pattern id="pg-site-tarmac" width={0.34} height={0.34}
                 patternUnits="userSpaceOnUse">
          <g fill="#5f5f5f">
            <circle cx={0.06} cy={0.09} r={0.032} />
            <circle cx={0.23} cy={0.05} r={0.022} />
            <circle cx={0.14} cy={0.24} r={0.028} />
            <circle cx={0.29} cy={0.28} r={0.020} />
          </g>
        </pattern>
        {/* hașura fină a locului de joacă (3.18): se citește ca suprafață
            amenajată, nu ca verde și nu ca pavaj */}
        <pattern id="pg-site-joaca" width={0.8} height={0.8}
                 patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <line x1={0} y1={0} x2={0} y2={0.8} stroke="#c9a227" strokeWidth={0.04} opacity={0.6} />
        </pattern>
        <clipPath id="pg-site-clip"><polygon points={lotPts} /></clipPath>
        {/* The parking aprons, so the planting breaks can be re-drawn THROUGH
            them: the pad is one continuous surface and the green shows in the
            gaps, rather than the pad being chopped up per stall group. */}
        <clipPath id="pg-site-apron">
          {runs.map((r, i) => (
            <polygon key={i} points={pts(runCorners(r))} />
          ))}
        </clipPath>
      </defs>

      {/* NO public street. The document does not carry one — it gives two
          `entries` points on the property line and nothing beyond it — and the
          band that used to be inferred here was built from the single lot edge
          nearest the access. On a surveyed ring that edge is short: 6.2 m on the
          scheme this was found on, against a 321.7 m frontage, so the band
          covered a fifth of the front and stopped in mid-air beside the
          entrance. It read exactly like a road half outside the plot, twice, to
          the person looking at it. An inference that gets mistaken for surveyed
          geometry is worse than a blank, so it is a blank. */}
      {/* ground */}
      <polygon points={lotPts} fill={A01.paper} />

      {/* everything paved/planted stays inside the property */}
      <g clipPath="url(#pg-site-clip)">
        {/* AMPRENTA SĂPĂTURII, sub tot restul.
            Verde deschis, ca fundal: e prima suprafață desenată, deci tot ce
            vine peste ea — asfalt, trotuare, plantare — o acoperă acolo unde
            există, iar ce rămâne verde deschis simplu e exact săpătura
            neacoperită de altceva. Plantarea de peste subsol are aceeași
            culoare, dar primește textura de iarbă deasupra, deci cele două se
            citesc separat: fundal neted = săpătură, textură = plantat. */}
        <Surfaces shapes={site.excavation} fill={A01.slab} />
        <Surfaces shapes={site.green?.deep} fill={A01.deep} />
        <Surfaces shapes={site.green?.over_slab} fill={A01.slab} />
        {/* ZONELE EXTERIOARE ale agentului (3.18, §37): suprafețe pline PESTE
            verde și SUB trotuare — verdele e tot ce rămâne nepavat, iar zona e
            o amenajare pe el. Loc de joacă galben pal cu hașură fină, platforma
            de gunoi maro deschis, piațeta crem; eticheta, mai jos, în afara
            clipului, cu LOD-ul etichetelor clădirilor. */}
        {(site.zones ?? []).map((z, i) => (
          // ȚINTĂ DE APĂSAT ca aleile: omul citește în panou felul și id-ul
          // agentului («Z1»); cea aleasă se conturează (Oana, 30.10.2026)
          <g key={`zn${i}`} data-strat="zona" data-fel={z.kind}
             {...pick({ kind: "siteZone", level: "site", i })}>
            <polygon points={pts(z.poly)}
              fill={z.kind === "playground" ? "#fff3b0" : z.kind === "waste_platform" ? "#d9c3a5" : "#f3ead8"}
              stroke={at("siteZone", i) ? A01.red : A01.hair} strokeWidth={at("siteZone", i) ? 0.35 : 0.08} />
            {z.kind === "playground" && (
              <polygon points={pts(z.poly)} fill="url(#pg-site-joaca)" />
            )}
          </g>
        ))}
        <Surfaces shapes={site.sidewalks} fill={A01.walk} />
        <Surfaces shapes={site.sidewalks} fill="url(#pg-site-pavers)" />
        {/* TROTUARELE, unul câte unul, ca ținte de apăsat peste pavaj (ca
            aleile): fișa spune id-ul agentului («T12»), lățimea și lungimea */}
        {interactive && (site.sidewalks ?? []).map((sh, i) => {
          const ring = Array.isArray(sh) ? sh : sh?.poly;
          if (!ring || ring.length < 3) return null;
          const holes = Array.isArray(sh) ? [] : (sh.holes ?? []);
          const on = at("siteSidewalk", i);
          return (
            <path key={`sw${i}`} d={pathD([ring, ...holes])} fillRule="evenodd" fill="transparent"
                  stroke={on ? A01.red : "none"} strokeWidth={on ? 0.3 : 0}
                  style={{ cursor: "pointer" }}
                  {...pick({ kind: "siteSidewalk", level: "site", i })} />
          );
        })}
        {/* The ramps go UNDER the carriageway. Every one of them runs a fifth of
            its area into the road — that is what a ramp does — so drawn on top
            its walls were a black box across the tarmac. Underneath, the road
            paints over the part inside it and the walls stop at the kerb, with
            nothing closing the mouth. */}
        {ramps.map((r, i) => (
          <RampMark key={`rp${i}`} r={r} showLabel={pxPerM >= LOD.sign} />
        ))}

        {/* Ordinary grey asphalt, not the basement's `var(--aisle)`. The pieces
            come ROUNDED from the engine (corners, ends, S bends), so their
            union is the carriageway outline. */}
        <path d={pathD(roads)} fill={A01.road} />
        {/* ALEILE, una câte una, ca ținte de apăsat peste umplutura comună: omul
            dă click pe o alee și citește în panou piesa («T3»), felul și nota
            pasului (Oana, 28.09.2026). Cea aleasă se conturează. */}
        {interactive && (site.roads ?? []).map((r, i) => {
          const ring = roadRing(r);
          if (!ring) return null;
          const on = at("siteRoad", i);
          return (
            <path key={`rd${i}`} d={pathD([ring])} fill="transparent"
                  stroke={on ? A01.red : "none"} strokeWidth={on ? 0.35 : 0}
                  style={{ cursor: "pointer" }}
                  {...pick({ kind: "siteRoad", level: "site", i })} />
          );
        })}
        {/* NO kerb line. It was one stroke per road RECTANGLE, and the roads
            overlap: at every crossroads each road's outline ran across the
            other's surface, boxing the junction. It would have drawn across the
            ramp mouths too, which is the one place the carriageway has to run
            straight in. Stroking the union of the network would be right, and
            there is no boolean library in this tree to compute it — but the
            reference sheet does not draw one either: its carriageway is plain
            grey and the edge is a change of colour. So is this one. */}

        {/* TRECERILE DE PIETONI (3.18): zebra peste asfalt, sub locuri și
            semne — oamenii au prioritate pe ea, deci se vede peste carosabil */}
        {d.zebre.map((z, i) => <ZebraTrecereMark key={`zt${i}`} dungi={z} />)}
        {/* trecerile ca ținte de apăsat: banda ÎNTREAGĂ a documentului (nu
            doar dungile), ca să se poată apăsa între ele; fișa spune aleea */}
        {interactive && (site.crossings ?? []).map((c, i) => {
          const on = at("siteCrossing", i);
          return (
            <path key={`cr${i}`} d={pathD([c.poly])} fill="transparent"
                  stroke={on ? A01.red : "none"} strokeWidth={on ? 0.3 : 0}
                  style={{ cursor: "pointer" }}
                  {...pick({ kind: "siteCrossing", level: "site", i })} />
          );
        })}

        {runs.map((r, i) => <ApronPad key={`ap${i}`} run={r} />)}
        {/* the planting breaks, showing through the apron */}
        <g clipPath="url(#pg-site-apron)">
          <Surfaces shapes={site.green?.deep} fill={A01.deep} />
          <Surfaces shapes={site.green?.over_slab} fill={A01.slab} />
        </g>
        {runs.map((r, i) => <ApronLines key={`al${i}`} run={r} />)}

        {/* the planting texture goes over both, so an island reads as planted */}
        <Surfaces shapes={site.green?.deep} fill="url(#pg-site-grass)" />
        <Surfaces shapes={site.green?.over_slab} fill="url(#pg-site-grass)" />

        {/* BENZILE COMUNE PMR (3.18): hașura fâșiei de transfer peste asfalt
            și verde, sub locuri — locul `ada` de 2,50 m de lângă ea rămâne o
            mașină obișnuită cu plăcuța lui (`SiteBay`) */}
        {(site.pmrStrips ?? []).map((b, i) => <FasiePmrPoly key={`pmr${i}`} poly={b.poly} />)}
        {/* Drawn PER ROW, but keyed and picked by `s.i` — the index into
            `site.stalls`, which is what a `stall` Selection carries. A bay whose
            geometry could not be recovered is simply not in a row, which is also
            how it stops putting NaN into the markup. */}
        {showCars && bayJSX}
        {treeJSX}
        {/* numărul scris în document (`n`, al agentului) bate numărătoarea
            desenului: omul spune agentului «locul 57» după plăcuța de aici */}
        {showNumbers && runs.flatMap((r) => r.stalls.map((s) => (
          <SiteStallNumber key={`sn${s.i}`} s={s} n={s.n ?? ++seq} />
        )))}

        {/* painted markings sit on the carriageway, under the signs */}
        {showSigns && d.car.axe.map((p, i) => <LaneDashes key={`ld${i}`} d={p} />)}
        {crossings.map((c, i) => <ZebraMark key={`zc${i}`} c={c} />)}
        {showSigns && marks.map((m, i) => <RoadArrowMark key={`rm${i}`} m={m} />)}
        {/* săgețile de sens ale aleilor cu SENS UNIC, pe axa lor (dreaptă sau
            curbă), în locul liniei punctate (`carosabil.sagetile`) */}
        {showSigns && d.car.sageti.map((m, i) => (
          <RoadArrowMark key={`su${i}`} m={{ ...m, kind: "straight" }} />
        ))}
        {/* the 5 % stretch of each ramp, over the road that may cover it */}
        {ramps.map((r, i) => (
          <RampMouthMark key={`rg${i}`} r={r} showLabel={pxPerM >= LOD.sign} />
        ))}
        {/* SCĂRILE DE EVACUARE ale parcajului care ies la teren (§35): cutia casei de
            scară ca o construcție la sol, cu zona fără parcare în jur */}
        {(site.scariEvacuare ?? []).map((s, i) => (
          <ScaraEvacuareMark key={`se${i}`} s={s} i={i} showLabel={pxPerM >= LOD.sign}
                             on={at("siteStair", i)} pick={pick} />
        ))}
      </g>

      {/* BALCOANELE CARE IES DIN AMPRENTĂ, sub barele clădirii.
          Desenate ÎNTRERUPT, fiindcă e convenția pentru ce se proiectează de
          deasupra: la sol nu există nimic acolo, dar volumul se avântă peste.
          Umplute translucid, ca să nu ascundă trotuarul sau verdele de sub ele
          — un balcon nu acoperă terenul, îl umbrește.
          SUB bare, deci partea care se suprapune cu clădirea dispare sub ea și
          rămâne vizibil exact cât iese, care e tot ce spune desenul. */}
      {(site.balconies ?? []).map((poly, i) => (
        <polygon key={`blc${i}`} points={pts(poly)}
                 fill={A01.ink} fillOpacity={0.06}
                 stroke={A01.ink} strokeWidth={0.12} strokeDasharray="0.6 0.4"
                 pointerEvents="none" />
      ))}
      {/* BALCOANELE DE LA PARTER: sunt la sol, nu o proiecție, deci contur plin
          și aceeași umplutură ca pridvorul clădirii (Oana, 28.09.2026). */}
      {(site.balconiiParter ?? []).map((poly, i) => (
        <polygon key={`blp${i}`} points={pts(poly)}
                 fill={A01.paved} stroke={A01.ink} strokeWidth={0.14}
                 pointerEvents="none" />
      ))}

      {/* SIMBOLUL ȘI ETICHETA ZONELOR (3.18): simbolul simplu al felului (leagăn,
          container, fântână) în centrul poligonului, cu pragul semnelor, și sub el
          numele felului din catalog, cu pragul etichetelor clădirilor; amândouă
          drepte pe ecran */}
      {(site.zones ?? []).map((z, i) => {
        const spot = labelSpot(z.poly);
        if (!spot) return null;
        const simbol = spot.r >= 1.6 && pxPerM >= LOD.sign;
        const fs = Math.min(1.2, (spot.r * 2 * 0.86) / (z.label.length * 0.62));
        const eticheta = fs * pxPerM >= LOD.barLabel && fs >= 0.45;
        if (!simbol && !eticheta) return null;
        // simbolul deasupra, eticheta dedesubt, pe verticala ecranului (−viewTurn)
        const dy = simbol && eticheta ? fs * 0.9 : 0;
        return (
          <g key={`zl${i}`} transform={`rotate(${-viewTurn} ${spot.x} ${spot.y})`}>
            {simbol && <ZonaSimbol kind={z.kind} x={spot.x} y={spot.y - dy} />}
            {eticheta && (
              // fără `dominant-baseline`: exportatorul PDF n-o știe, deci centrarea
              // pe verticală e un `dy` de o treime de corp, ca la celelalte texte
              <text x={spot.x} y={spot.y + dy + fs * 0.35 + (simbol ? 0.6 : 0)} fontSize={fs} fontFamily={SANS}
                fill={A01.ink} textAnchor="middle" {...DECOR}>
                {z.label}
              </text>
            )}
          </g>
        );
      })}

      {/* the building bands sit on top of their sidewalks */}
      {(site.bars ?? []).map((b, i) => (
        <BarView key={`bar${i}`} pts={b.pts} label={b.label} h={b.h} code={b.code}
          functiune={b.functiune} pxPerM={pxPerM}
          pick={pick({ kind: "siteBar", level: "site", i })}
          selected={at("siteBar", i)} />
      ))}

      {/* property line: the long-dash-dot-dash convention, in red */}
      <polygon points={lotPts} fill="none" stroke={A01.red}
        strokeWidth={0.25} strokeDasharray="2 0.7 0.3 0.7" strokeLinejoin="round" />

      {/* One pickable side per PlotEdge, over the property line and invisible.
          The setback rules are counted per side, so a side has to be selectable:
          before this, four findings all pointed at "the site" and clicking one
          highlighted the whole plot. A transparent 2 m band is the hit area — the
          drawn line is 0,25 m and nobody can press that — and the selected side
          gets a visible highlight. */}
      {/* Gated on `interactive`: these are HIT TARGETS. The static export path
          has nothing to press, so four 2 m transparent strokes on the sheet are
          weight with no reader — and 2 m is the pixel-space convention, which
          is exactly what does not survive an exporter with no
          `non-scaling-stroke`. */}
      {interactive && (site.edges ?? []).map((e) => {
        const ax = e.axis ?? [];
        if (ax.length < 2) return null;
        const on = at("plotEdge", e.i);
        return (
          <g key={`pe${e.i}`}>
            <line x1={ax[0][0]} y1={ax[0][1]} x2={ax[1][0]} y2={ax[1][1]}
              stroke="transparent" strokeWidth={2} strokeLinecap="butt"
              {...pick({ kind: "plotEdge", level: "site", i: e.i })} />
            {on && (
              <line x1={ax[0][0]} y1={ax[0][1]} x2={ax[1][0]} y2={ax[1][1]}
                stroke={A01.red} strokeWidth={0.8} strokeOpacity={0.85}
                strokeLinecap="butt" style={{ pointerEvents: "none" }} />
            )}
          </g>
        );
      })}

      {showSigns && signs.map((s, i) => <SignPlate key={`sg${i}`} s={s} />)}
      {/* PORȚILE pe limita terenului (3.18): semn mic ca plăcuțele, ascuns cu ele */}
      {showSigns && (site.gates ?? []).map((p, i) => <GateMark key={`gt${i}`} p={p} />)}

      {(site.entries ?? []).filter(at2).map(([x, y], i) => (
        <EntryMark key={`en${i}`} x={x} y={y} cx={lcx} cy={lcy} />
      ))}

      {/* one "acces clădire" per core, on the parking-band side — the arrow only
          while the plan has no real block doors; with them, the doors say it */}
      {(site.entrances ?? []).map((e, i) => (
        <BuildingEntrance key={`ac${i}`} e={e} pxPerM={pxPerM}
          faraSageata={(site.usiBloc?.length ?? 0) > 0}
          pick={pick({ kind: "siteEntrance", level: "site", i })}
          selected={at("siteEntrance", i)} />
      ))}
      {/* UȘILE DE INTRARE ÎN BLOC: ușă dublă deschisă spre curte, arcurile de
          balans și săgeata albastră de intrare (Oana, 29–30.09.2026) */}
      {(site.usiBloc ?? []).map((u) => <UsaBloc key={u.eid} u={u} />)}

      {/* --- sheet furniture ------------------------------------------- */}
      {/* the boundary schedule: every corner numbered, every run measured */}
      {vertices.map((v) => <VertexMark key={`vx${v.i}`} v={v} />)}
      {edgeRuns.map((d, i) => <DimSpanView key={`br${i}`} d={d} fs={1.1} />)}
      {/* the stall pitch, only once the sheet is read close enough for it */}
      {showChains && runs.flatMap((r, i) =>
        runChain(r).map((d, j) => (
          <DimSpanView key={`dc${i}-${j}`} d={d} fs={0.8} />
        )))}

      {/* COTELE AȘEZĂRII: între corpuri și până la limită.
          O IERARHIE DE MĂRIMI, nu o a doua culoare. Planșa are deja un singur
          vocabular de cotare — linie roșie cu pastilă — iar un al doilea ar
          pune cititorul să întrebe ce înseamnă fiecare. Ce le deosebește e
          corpul literei, ca pe o planșă desenată de mână: conturul cel mai mare
          (1,1), lungimea și lățimea corpului (1,0, cu linii ajutătoare, în metri —
          cerută «clar», deci cu cuvântul și unitatea), între corpuri mai mic (0,95), retragerea și mai mic (0,85),
          pasul locurilor, parcajul și aleea cel mai mic (0,8). */}
      {showCote && d.cote.dimensiuni.map((c, i) => (
        <CotaCorpView key={`cg${i}`} c={c} fs={1.0} />
      ))}
      {showCote && d.cote.intreCorpuri.map((c, i) => (
        <DimSpanView key={`cc${i}`} d={c} fs={0.95} />
      ))}
      {showCote && d.cote.retrageri.map((c, i) => (
        <DimSpanView key={`cr${i}`} d={c} fs={0.85} />
      ))}
      {showCote && d.cote.parcaje.map((c, i) => (
        <DimSpanView key={`cp${i}`} d={c} fs={0.8} />
      ))}
      {showCote && d.cote.drumuri.map((c, i) => (
        <DimSpanView key={`cd${i}`} d={c} fs={0.8} />
      ))}

      {/* ZONA INTERZISĂ. Un singur `<path>` pentru toată hașura — v. nota din
          `hasura.ts`: câteva sute de `<line>` s-ar simți la panoramare. Peste
          ea, conturul zonei edificabile, punctat, ca să se vadă unde se termină
          interdicția și nu doar că e hașurat. */}
      {arataRetrageri && d.interzis.length > 0 && (
        <g pointerEvents="none" data-strat="retrageri">
          <path d={caleHasura(d.interzis)} fill="none" stroke={A01.red}
                strokeWidth={0.22} opacity={0.4} />
          {(site.edificabil ?? []).map((inel, i) => (
            <polygon key={`ze${i}`} points={pts(inel)} fill="none"
                     stroke={A01.red} strokeWidth={0.35} opacity={0.75}
                     strokeDasharray="1.6 1.2" />
          ))}
        </g>
      )}

      {/* ZONELE DE REGIM (3.22, §41): conturul fiecărei zone, punctat, cu
          numele ei la un colț. Mereu aprins, nu doar cu retragerile: regimul
          diferit de o parte și de alta a liniei e motivul pentru care corpurile
          vecine au alt P+n, și fără linie planșa nu-l explică. Linia nu e
          limită de proprietate (nicio retragere pe ea), de aceea nu e roșie. */}
      {(site.zoneRegim ?? []).map((z, i) => {
        const sus = z.pts.reduce((m, p) => (p[1] < m[1] || (p[1] === m[1] && p[0] < m[0]) ? p : m), z.pts[0]);
        return (
          <g key={`zr${i}`} pointerEvents="none" data-strat="zone-regim">
            <polygon points={pts(z.pts)} fill="none" stroke={A01.ink}
                     strokeWidth={0.3} opacity={0.8} strokeDasharray="2.4 1.2 0.6 1.2" />
            {z.nume && (
              <text x={sus[0] + 1.2} y={sus[1] + 2.6} fontSize={1.6} fill={A01.ink}
                    fontFamily="Helvetica, Arial, sans-serif" fontStyle="italic">{z.nume}</text>
            )}
          </g>
        );
      })}

      {/* The sheet furniture goes BELOW everything drawn, the inferred street
          included. It used to sit at `maxY + 3`, which is inside the 16 m street
          band whenever the access is on that side — so the scale bar and the
          legend landed on the carriageway right beside the entry, and the whole
          corner read as though the road were spilling out of the plot. */}
      {showCompass && <CompassRose x={minX + 9} y={compassY} r={4.4} />}
      {showSheet && <SiteScaleBar x={minX} y={furnitureY} />}
      {showSheet && (
        <GreenBalance areas={site.areas ?? {}} x={minX} y={furnitureY + 6} />
      )}
    </g>
    </ViewTurn.Provider>
  );
}

/** MEMOIZAT: pânza se re-randează la fiecare cadru de panoramare și zoom, iar
 *  proprietățile de aici nu se schimbă între cadre (scara vine așezată, v.
 *  `useScaraDetaliului`). Fără `memo` desenul se reconcilia de la capăt de
 *  60 de ori pe secundă. */
export default memo(SitePlanView);
